import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { catchError, Observable, of } from 'rxjs';
import { switchMap, map, filter } from 'rxjs';
import { warnOnErrorWithRetryOption$ } from '@core/services/project/project-detail.effects';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Action, Store } from '@ngrx/store';
import { normalize } from 'normalizr';
import { selectIsSyrfAdminUserAuthenticated } from '../../auth/auth.selectors';
import { AdminEmailHttpService, InvestigatorFullDto, InvestigatorHttpService } from '@core/services/api-client.generated';
import { AppConfigService } from '../config/app-config.service';
import { authApiActions } from '@core/auth/auth.actions';
import { IFlatInvestigator } from '@app/admin/admin.component';
import { adminInvestigatorSchema } from '@core/state/entities/admin-investigators/admin-investigator.reducer';
import { adminActions } from '@core/actions/admin.actions';
import { emailTemplateSchema } from '@core/state/entities/email-templates/email-template.entity';
import { throwErrorExp } from '@core/actions/util';

const toFlatInvestigator = (
  investigator: InvestigatorFullDto
): IFlatInvestigator => ({
  id: investigator.id,
  first: investigator.fullName?.first ?? throwErrorExp("FullName should exist here"),
  surname: investigator.fullName.surname,
  preferred: investigator.fullName.preferred,
  pictureUrl: investigator.pictureUrl,
  primaryEmail: investigator.secureDetails.primaryEmail,
  emails: investigator.secureDetails.emails,
  newEmailVerifiers: investigator.secureDetails.newEmailVerifiers,
  completeness: investigator.completeness,
  dateTimeCreated: investigator.dateTimeCreated,
  audit: investigator.audit,
});

@Injectable()
export class AdminEffects {
  getInvestigators$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(authApiActions.loadInvestigators, authApiActions.signInRecorded),
      filter((_) => this._appConfig.config.authProvider === 'auth0'),
      switchMap(() =>
        this._store
          .select(selectIsSyrfAdminUserAuthenticated)
          .pipe(filter((isAdmin) => isAdmin))
      ),
      switchMap(() =>
        this._investigatorService
          .getInvestigators()
          .pipe(
            warnOnErrorWithRetryOption$(
              this._snackbar,
              'There was a problem loading the list of SyRF investigators.'
            )
          )
      ),
      map((investigators) =>
        authApiActions.investigatorsLoaded(
          normalize(
            investigators?.map((investigatorFullDto) =>
              toFlatInvestigator(investigatorFullDto)
            ),
            [adminInvestigatorSchema]
          )
        )
      )
    );
  });

  emailTemplates$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(adminActions.loadEmailTemplates),
      switchMap(() =>
        this._emailAdminService.getEmailTemplates().pipe(
          warnOnErrorWithRetryOption$(
            this._snackbar,
            'There was a problem loading the list of SyRF email templates.'
          ),
          map((emailTemplates) =>
            adminActions.emailTemplatesLoaded(
              normalize(emailTemplates, [emailTemplateSchema])
            )
          ),
          catchError((error) =>
            of(adminActions.emailTemplatesLoadFailed({ error }))
          )
        )
      )
    );
  });

  updateEmailTemplate$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(adminActions.updateEmailTemplate),
      switchMap(({ templateName, template }) =>
        this._emailAdminService
          .updateEmailTemplate(templateName, template)
          .pipe(
            warnOnErrorWithRetryOption$(
              this._snackbar,
              'There was a problem updating the SyRF email template.'
            ),
            map((emailTemplate) =>
              adminActions.emailTemplateUpdated(
                normalize(emailTemplate, emailTemplateSchema)
              )
            ),
            catchError((error) =>
              of(adminActions.emailTemplateUpdateFailed({ error }))
            )
          )
      )
    );
  });

  createEmailTemplate$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(adminActions.createEmailTemplate),
      switchMap(({ templateName, template }) =>
        this._emailAdminService.createEmailTemplate(template).pipe(
          warnOnErrorWithRetryOption$(
            this._snackbar,
            'There was a problem creating the SyRF email template.'
          ),
          map((emailTemplate) =>
            adminActions.emailTemplateCreated(
              normalize(emailTemplate, emailTemplateSchema)
            )
          ),
          catchError((error) =>
            of(adminActions.emailTemplateCreateFailed({ error }))
          )
        )
      )
    );
  });

  deleteEmailTemplate$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(adminActions.deleteEmailTemplate),
      switchMap(({ templateName }) =>
        this._emailAdminService.deleteEmailTemplate(templateName).pipe(
          warnOnErrorWithRetryOption$(
            this._snackbar,
            'There was a problem deleting the SyRF email template.'
          ),
          map((emailTemplate) =>
            adminActions.emailTemplateDeleted({
              id: templateName,
            })
          ),
          catchError((error) =>
            of(adminActions.emailTemplateDeleteFailed({ error }))
          )
        )
      )
    );
  });

  constructor(
    private _actions$: Actions,
    private _snackbar: MatSnackBar,
    private _appConfig: AppConfigService,
    private _store: Store,
    private _investigatorService: InvestigatorHttpService,
    private _emailAdminService: AdminEmailHttpService
  ) {}
}
